import { WarningOutlined } from '@ant-design/icons'
import React from 'react'
import styled from 'styled-components'
import { Button } from 'sutra/components/Button'

export interface StaleFilterValuesNoticeProps {
  /** The selected values the project no longer offers, across every owned
   *  filter — named in full, since which values are gone is the point. */
  values: readonly string[]
  /** The subset selected by an `is not` filter: excluding a value that no
   *  longer exists excludes nothing, so it can't be why a chart is empty. */
  excludedValues: readonly string[]
  /** Labels of the filters that "Remove" would leave with no values, and so
   *  delete outright. */
  emptiedFilters: readonly string[]
  /** Who owns these filters: a tile's emptied filter falls back to the
   *  dashboard's for that field; a dashboard filter is just gone. */
  scope: 'tile' | 'dashboard'
  onRemove: () => void
}

const list = (items: readonly string[]): string =>
  items.length <= 1
    ? (items[0] ?? '')
    : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`

/** "Filtering on them matches no data" — true only for `is` values; an
 *  `is not` on a missing value has no effect. */
function consequence(
  values: readonly string[],
  excluded: readonly string[],
): string {
  const included = values.filter((v) => !excluded.includes(v))
  if (!excluded.length) {
    return `Filtering on ${values.length === 1 ? 'it' : 'them'} matches no data.`
  }
  if (!included.length) {
    return `Excluding ${values.length === 1 ? 'it' : 'them'} has no effect.`
  }
  return `Including ${list(included)} matches no data; excluding ${list(excluded)} has no effect.`
}

/** What "Remove" does beyond dropping the values: an emptied filter is
 *  deleted, which on a tile hands the field back to the dashboard. */
function removalEffect(
  emptied: readonly string[],
  scope: 'tile' | 'dashboard',
  pronoun: string,
): string | null {
  if (!emptied.length) return null
  const names = `${list(emptied)} filter${emptied.length === 1 ? '' : 's'}`
  return scope === 'tile'
    ? `Removing ${pronoun} empties the ${names}, so this tile will use the dashboard's ${names} instead, if there ${emptied.length === 1 ? 'is one' : 'are any'}.`
    : `Removing ${pronoun} empties the ${names}, so ${emptied.length === 1 ? 'it is' : 'they are'} removed from the dashboard.`
}

/**
 * Sits above (or atop) a filter list whose selections include values the
 * project no longer has (e.g. a brand whose data was removed). Names the
 * values, says what they do to the data and what removing them will do, and
 * offers the one-click cleanup. The only stale-values explanation a surface
 * should show on hover — a badge next to it must not open a second popup.
 */
export const StaleFilterValuesNotice: React.FC<
  StaleFilterValuesNoticeProps
> = ({ values, excludedValues, emptiedFilters, scope, onRemove }) => {
  const pronoun = values.length === 1 ? 'it' : 'them'
  const effect = removalEffect(emptiedFilters, scope, pronoun)
  return (
    <Notice role="status">
      <IconWrap>
        <WarningOutlined />
      </IconWrap>
      <Body>
        <span>
          <Values>{values.join(', ')}</Values>{' '}
          {values.length === 1 ? 'no longer exists' : 'no longer exist'} in this
          project. {consequence(values, excludedValues)}
          {effect && ` ${effect}`}
        </span>
        <RemoveButton variant="link" size="xs" onClick={onRemove}>
          Remove {pronoun}
        </RemoveButton>
      </Body>
    </Notice>
  )
}

const Notice = styled.div`
  display: flex;
  gap: 8px;
  max-width: 360px;
  padding: 8px 12px;
  border: 1px solid #ffd591;
  border-radius: 6px;
  background: #fffbe6;
  font-size: 12px;
  line-height: 18px;
  color: #613400;
`

const IconWrap = styled.div`
  color: #d46b08;
`

const Body = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
`

const Values = styled.span`
  font-weight: 600;
`

const RemoveButton = styled(Button)`
  padding: 0;
  height: auto;
  font-size: 12px;
`
